import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "utils/redux/store";
import { responseStatus } from "utils/types";
import { request } from "utils/request";
import { getDepartmentLeadId } from "app/containers/AdminConsole/helpers";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import {
  ActivityCacheOwner,
  getActivityCompanyKey,
  getActivityCompanyQuery,
  initialActivityCacheOwner,
  shouldFetchActivityData,
  startActivityRequest,
} from "utils/activityTableCache";
import { GenderInsightsResponse } from "app/containers/DepartmentInsightReport/types";
import { getDepartmentInsightReport } from "app/containers/DepartmentInsightReport/slice";
import { getDepartmentResultsInfo } from "app/containers/Dashboard/slice";
import {
  CompanySettings,
  Department,
  DepartmentType,
  ScheduleSurveyPayload,
  AnalyticsText,
  TextInsightResponse,
  TextSynthesisResponse,
  WordCloudResponse,
  TeamAndDepartmentLeadsResponse,
  CompanySettingPayload,
  AssessmentInformation,
  CheckSingleEmailResponse,
  TestTakerMatch,
  CompanyVisibilitySettingPayload,
  PermissionDefaultUpdatedEntities,
  PendingTeamMember,
  TeamScanFeedbackResponse,
  ActivityTablePayload,
  OverallActivityTableRow,
  AllTEAMscanActivityTableData,
  GetTEAMscanActivityTablePayload,
  TimeIntervalTEAMscanActivityTableData,
  AllOverallActivityTableData,
  AllEppActivityTableData,
  GetEppActivityTablePayload,
  EppActivityTableData,
  TeamsTestMessageResponse,
  IntegrationStatus,
} from "./types";
import {
  getTeamAnalyticsText,
  getTeamDisplayStatusByTeamId,
  getTeamAssessmentInstances as getTeamAssessmentInstancesByTeamId,
  clearTeamVisibilitySettings,
} from "app/containers/TeamGuide/slice";
import {
  StartAndEndDateOrInstance,
  OpenAIUrlParams,
} from "app/components/SurveyDataInstances/types";
import { onSuccessfulDepartmentCreation } from "app/components/Modals/slice";
import {
  getAppendedUrl,
  getAppendedOpenAIUrl,
} from "app/components/InstancePicker/helpers";
import { TIME_INTERVAL_OPTIONS } from "app/components/InstancePicker/constants";
import { getTeam360Scores } from "app/components/SurveyDataInstances/slice";
import { InstanceShowing } from "app/components/InstancePicker/types";
import { resetUserVisibilitySettings } from "app/containers/UserSettings/slice";
import {
  PutAssessmentPayload,
  TeamAssessmentInstance,
} from "app/containers/Assessment/types";
import {
  getAssessmentInstances,
  getMostRecentAssessmentInstances,
} from "app/containers/Assessment/slice";
import {
  getNotifications,
  onLaunchTeamScanForTeamIdsDismissNotification,
} from "app/components/Notifications/slice";
import {
  getAllTeamsByCompanyAccountId,
  getUserInfoById,
  removeUsersFromCreatedButNotInvitedUsersIds,
  updateTeamsAction,
  updateUsersAction,
} from "../Global/slice";
import { UserInfo } from "../Global/types";
import { updateEntitiesBasedOnChangesToCustomList } from "../WeeklyCheckInMonitor/helpers";
import {
  UpdateOrganizationalStructurePayload,
  UpdateOrganizationalStructureResponse,
} from "app/components/DeactivateUserModal/types";

// ------------------ State Type/Structure ------------------
export interface AdminConsoleState {
  companySettings: null | CompanySettings;
  departments: { [departmentId: number]: Department };
  departmentTypes: {
    [departmentTypeId: number]: DepartmentType;
  };
  analyticsText: AnalyticsText | null;
  sampleAnalyticsText: AnalyticsText | null;
  feedbackResponsesCount: number | null;
  textInsights: TextInsightResponse | null;
  textInsightsErrorMessage: string | null;
  textSynthesis: TextSynthesisResponse | null;
  genderInsights: GenderInsightsResponse | null;
  wordCloud: WordCloudResponse | null;
  teamDepartmentLeads: null | TeamAndDepartmentLeadsResponse;
  companyInviteLink: string;
  team360InstancePicked: InstanceShowing;
  getTeamAndDepartmentLeadsCalled: boolean;
  organizationTeamAssessmentInstances: TeamAssessmentInstance[];
  assessmentInformation: AssessmentInformation | null;
  team360CardsExpanded: {
    [cardTitle: string]: boolean;
  };
  isDepartmentsHidden: boolean;
  aiFeaturesEnabled: boolean;
  hasCoachBoConversationHistoryEnabled: boolean;
  newCheckedEmails: {
    [email: string]: CheckSingleEmailResponse;
  };
  inviteCreatedUsersStatus: responseStatus;
  testTakeQueriedMatches: {
    [query: string]: TestTakerMatch[];
  };
  isInviteModalOpen: {
    isOpen: boolean;
    teamId?: number;
    defaultTab?: "csv" | "email" | "link";
  } | null;
  userVisibilityUpdatedEntities: {
    [
      visibilityPermissionDefaultId: number
    ]: PermissionDefaultUpdatedEntities | null;
  };
  teamMembersWithPendingTeamScanSurvey: { userAccountId: number }[];
  deactivatedUsers: Record<number, UserInfo>;
  pendingTeamMemberships: PendingTeamMember[];
  checkedEditedEmails: {
    [email: string]: CheckSingleEmailResponse;
  };
  teamScanFeedback: TeamScanFeedbackResponse | null;
  getTeamScanFeedbackStatus: responseStatus;
  allTEAMscanActivityTableData: AllTEAMscanActivityTableData;
  allOverallActivityTableData: AllOverallActivityTableData;
  allEppActivityTableData: AllEppActivityTableData;
  overallActivityTableDataOwner: ActivityCacheOwner;
  TEAMscanActivityTableDataOwner: ActivityCacheOwner;
  eppActivityTableDataOwner: ActivityCacheOwner;
  hideSurveyTableBanner: boolean;
  integrationStatus: {
    [provider: string]: IntegrationStatus;
  };
  fetchIntegrationStatusStatus: {
    [provider: string]: responseStatus;
  };
  deactivateAutomaticTeamStatus: responseStatus;
  getOverallActivityTableDataStatus: responseStatus;
  getTEAMscanActivityTableDataStatus: responseStatus;
  getEppActivityTableDataStatus: responseStatus;
  invitingTeamMembersWithPendingTeamScanSurveyStatus: responseStatus;
  getPendingTeamMembershipsStatus: responseStatus;
  getVisibilityPermissionDifferCountStatus: responseStatus;
  updateVisibilitySettingsStatus: responseStatus;
  getTestTakerMatchesStatus: responseStatus;
  getTeamAssessmentInstancesStatus: responseStatus;
  gettingAssessmentInformationStatus: responseStatus;
  updatingTeam360DateRangeStatus: responseStatus;
  getGenderInsightsStatus: responseStatus;
  getTeamAndDepartmentLeadsStatus: responseStatus;
  scheduleTEAM360Status: responseStatus;
  getTextSynthesisStatus: responseStatus;
  getTextInsightsStatus: responseStatus;
  getWordCloudStatus: responseStatus;
  getAnalyticsTextStatus: responseStatus;
  getSampleAnalyticsTextStatus: responseStatus;
  getCompanySettingsStatus: responseStatus;
  updateCompanySettingsStatus: responseStatus;
  getDepartmentsStatus: responseStatus;
  createDepartmentStatus: responseStatus;
  createdDepartmentId: number | null;
  createCompanyInviteLinkStatus: responseStatus;
  deleteDepartmentStatus: responseStatus;
  getDepartmentTypesStatus: responseStatus;
  updateDepartmentStatus: responseStatus;
  getFeedbackResponsesCountStatus: responseStatus;
  checkSingleEmailStatus: responseStatus;
  bulkUpdatePendingTeamMembershipsStatus: responseStatus;
  getAllDeactivatedUsersStatus: responseStatus;
  checkUserEmailStatus: responseStatus;
  updateCompanyTimezoneStatus: responseStatus;
}

// ------------------ InitialState ------------------
const initialState: AdminConsoleState = {
  companySettings: null,
  departments: {},
  departmentTypes: {},
  analyticsText: null,
  sampleAnalyticsText: null,
  textInsights: null,
  feedbackResponsesCount: null,
  textInsightsErrorMessage: null,
  genderInsights: null,
  textSynthesis: null,
  wordCloud: null,
  teamDepartmentLeads: null,
  companyInviteLink: "",
  getTeamAndDepartmentLeadsCalled: false,
  assessmentInformation: null,
  team360InstancePicked: {},
  organizationTeamAssessmentInstances: [],
  team360CardsExpanded: {},
  createdDepartmentId: null,
  isDepartmentsHidden: false,
  newCheckedEmails: {},
  aiFeaturesEnabled: true,
  testTakeQueriedMatches: {},
  isInviteModalOpen: null,
  userVisibilityUpdatedEntities: {},
  hasCoachBoConversationHistoryEnabled: true,
  hideSurveyTableBanner: false,
  teamMembersWithPendingTeamScanSurvey: [],
  pendingTeamMemberships: [],
  deactivatedUsers: {},
  checkedEditedEmails: {},
  teamScanFeedback: null,
  getTeamScanFeedbackStatus: "idle",
  allTEAMscanActivityTableData: {},
  allOverallActivityTableData: {},
  allEppActivityTableData: {},
  overallActivityTableDataOwner: initialActivityCacheOwner,
  TEAMscanActivityTableDataOwner: initialActivityCacheOwner,
  eppActivityTableDataOwner: initialActivityCacheOwner,
  integrationStatus: {},
  fetchIntegrationStatusStatus: {},
  deactivateAutomaticTeamStatus: "idle",
  getTEAMscanActivityTableDataStatus: "idle",
  getOverallActivityTableDataStatus: "idle",
  getEppActivityTableDataStatus: "idle",
  getPendingTeamMembershipsStatus: "idle",
  getVisibilityPermissionDifferCountStatus: "idle",
  invitingTeamMembersWithPendingTeamScanSurveyStatus: "idle",
  inviteCreatedUsersStatus: "idle",
  getTestTakerMatchesStatus: "idle",
  updateVisibilitySettingsStatus: "idle",
  gettingAssessmentInformationStatus: "idle",
  getGenderInsightsStatus: "idle",
  getTeamAssessmentInstancesStatus: "idle",
  updatingTeam360DateRangeStatus: "idle",
  getTeamAndDepartmentLeadsStatus: "idle",
  scheduleTEAM360Status: "idle",
  getTextSynthesisStatus: "idle",
  getTextInsightsStatus: "idle",
  getWordCloudStatus: "idle",
  getAnalyticsTextStatus: "idle",
  getSampleAnalyticsTextStatus: "idle",
  getCompanySettingsStatus: "idle",
  updateCompanySettingsStatus: "idle",
  getDepartmentsStatus: "idle",
  createDepartmentStatus: "idle",
  createCompanyInviteLinkStatus: "idle",
  deleteDepartmentStatus: "idle",
  getDepartmentTypesStatus: "idle",
  updateDepartmentStatus: "idle",
  getFeedbackResponsesCountStatus: "idle",
  checkSingleEmailStatus: "idle",
  bulkUpdatePendingTeamMembershipsStatus: "idle",
  getAllDeactivatedUsersStatus: "idle",
  checkUserEmailStatus: "idle",
  updateCompanyTimezoneStatus: "idle",
};

// ------------------ Helper Thunks ------------------
export const updateTeam360DateRange = createAsyncThunk(
  "adminConsole/updateTeam360DateRange",
  async (
    {
      startDate,
      endDate,
      instance,
      instanceType,
      throwErrorIfNoOverview,
    }: {
      instanceType?: (typeof TIME_INTERVAL_OPTIONS)[number];
      throwErrorIfNoOverview?: boolean;
    } & StartAndEndDateOrInstance,
    thunkAPI
  ) => {
    const {
      global: { companyInfo },
    } = thunkAPI.getState() as RootState;

    if (!companyInfo?.companyAccountId) {
      return { startDate, endDate, instanceType, instance };
    }
    const { companyAccountId } = companyInfo;
    const dateRange = { startDate, endDate, instance };

    const resp = (await thunkAPI.dispatch(
      getAnalyticsText({ ...dateRange, throwErrorIfNoOverview })
    )) as { error?: any };

    if (resp?.error) {
      throw new Error("No overview found");
    }

    await Promise.all([
      thunkAPI.dispatch(getGenderInsights({ ...dateRange, companyAccountId })),
      thunkAPI.dispatch(getTeam360Scores({ ...dateRange })),
    ]);
    return { startDate, endDate, instanceType, instance };
  }
);

// ------------------------------------ GET Requests ------------------------------------
export const getCompanySettings = createAsyncThunk(
  "adminConsole/getCompanySettings",
  async (companyAccountId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/companies/${companyAccountId}/settings`;
    const { settings } = (await request(requestUrl)) as {
      settings: CompanySettings;
    };
    return settings;
  }
);

export const getDepartments = createAsyncThunk(
  "adminConsole/getDepartments",
  async ({
    companyAccountId,
    companySelector,
  }: {
    companyAccountId: number;
    forceFetch?: boolean;
    companySelector?: string;
  }) => {
    const requestUrl = companySelector
      ? `${REACT_APP_API_URL}/talentInsights/companies/${companyAccountId}/departments?companySelector=${encodeURIComponent(companySelector)}`
      : `${REACT_APP_API_URL}/talentInsights/companies/${companyAccountId}/departments`;
    const getDepartmentsResponse = (await request(requestUrl)) as {
      departments: { [departmentId: number]: Department };
    };
    return getDepartmentsResponse.departments;
  },
  {
    condition: ({ forceFetch }, { getState }) => {
      const {
        adminConsole: { getDepartmentsStatus },
      } = getState() as RootState;
      if (getDepartmentsStatus !== "idle" && !forceFetch) {
        return false;
      }
    },
  }
);

export const getDepartmentTypes = createAsyncThunk(
  "adminConsole/getDepartmentTypes",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departmentTypes`;
    const getDepartmentTypesResponse = (await request(requestUrl)) as {
      departmentTypes: DepartmentType[];
    };
    return getDepartmentTypesResponse.departmentTypes;
  },
  {
    // Fetched on demand (department table, edit department modal), so only fetch once.
    condition: (_, { getState }) => {
      const {
        adminConsole: { getDepartmentTypesStatus },
      } = getState() as RootState;
      if (
        getDepartmentTypesStatus === "loading" ||
        getDepartmentTypesStatus === "succeeded"
      ) {
        return false;
      }
    },
  }
);

export const getAssessments = createAsyncThunk(
  "adminConsole/getAssessments",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/assessments`;
    const getAssessmentsResponse = await request(requestUrl);
    return getAssessmentsResponse;
  }
);

export const getSampleAnalyticsText = createAsyncThunk(
  "adminConsole/getSampleAnalyticsText",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/sampleOrganizationInsightReport`;
    const getSampleAnalyticsTextResponse = (await request(
      requestUrl
    )) as AnalyticsText;
    return getSampleAnalyticsTextResponse;
  }
);

export const getAnalyticsText = createAsyncThunk(
  "adminConsole/getAnalyticsText",
  async (
    payload:
      | undefined
      | (StartAndEndDateOrInstance & {
          throwErrorIfNoOverview?: boolean;
        })
  ) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/getOrganizationInsightReport${getAppendedUrl(
      {
        ...payload,
      }
    )}`;

    const getAnalyticsTextResponse = (await request(
      requestUrl
    )) as AnalyticsText;

    if (
      payload?.throwErrorIfNoOverview &&
      !getAnalyticsTextResponse?.overview &&
      !getAnalyticsTextResponse?.completionInfo?.totalInvited
    ) {
      throw new Error("No overview found");
    }

    return getAnalyticsTextResponse;
  },
  {
    // This condition might cause problems incase the user wants to see analyticsText a different time interval
    // because if we are fetching for a different time interval, we will not fetch the analyticsText
    condition: (_, { getState }) => {
      const {
        adminConsole: { getAnalyticsTextStatus },
      } = getState() as RootState;
      if (getAnalyticsTextStatus === "loading") {
        return false;
      }
    },
  }
);

export const getGenderInsights = createAsyncThunk(
  "adminConsole/getGenderInsights",
  async ({
    companyAccountId,
    startDate,
    endDate,
    instance,
  }: { companyAccountId: number } & StartAndEndDateOrInstance) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/genderAnalysis/organization/${companyAccountId}${getAppendedUrl(
      {
        startDate,
        endDate,
        instance,
      }
    )}`;
    const getGenderInsightsResponse = (await request(
      requestUrl
    )) as GenderInsightsResponse;
    return getGenderInsightsResponse;
  }
);

export const getTextSynthesis = createAsyncThunk(
  "adminConsole/getTextSynthesis",
  async (
    payload: undefined | OpenAIUrlParams,
    { getState, rejectWithValue }
  ) => {
    const isAiFeaturesEnabled = (getState() as RootState).adminConsole
      .aiFeaturesEnabled;
    if (!isAiFeaturesEnabled) {
      return rejectWithValue("AI features are disabled");
    }

    const requestUrl = `${REACT_APP_API_URL}/talentInsights/generateSynthesis${getAppendedOpenAIUrl(
      {
        ...payload,
      }
    )}`;
    const getTextSynthesisResponse = (await request(
      requestUrl
    )) as TextSynthesisResponse;

    return getTextSynthesisResponse;
  }
);

export const getWordCloud = createAsyncThunk(
  "adminConsole/getWordCloud",
  async (payload: undefined | StartAndEndDateOrInstance) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/getWordCloud${getAppendedUrl(
      {
        ...payload,
      }
    )}`;
    const getWordCloudResponse = (await request(
      requestUrl
    )) as WordCloudResponse;
    return getWordCloudResponse;
  },
  {
    condition: (_, { getState }) => {
      const {
        adminConsole: { getWordCloudStatus },
      } = getState() as RootState;
      if (getWordCloudStatus === "loading") {
        return false;
      }
    },
  }
);

export const getTextInsights = createAsyncThunk(
  "adminConsole/getTextInsights",
  async (payload: undefined | OpenAIUrlParams) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/TEAMScoreSummary${getAppendedOpenAIUrl(
      {
        ...payload,
      }
    )}`;
    const getTextInsightsResponse = (await request(
      requestUrl,
      { method: "GET" },
      true
    )) as TextInsightResponse & { status?: number };

    const textInsightsError = getTextInsightsError(getTextInsightsResponse);
    if (textInsightsError) {
      throw new Error(textInsightsError);
    }
    return getTextInsightsResponse;
  }
);

export const getTEAMscanFeedbackResponses = createAsyncThunk(
  "adminConsole/getTEAMscanFeedbackResponses",
  async (payload: undefined | OpenAIUrlParams) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/TEAMScanFeedback${getAppendedOpenAIUrl(
      {
        ...payload,
      }
    )}`;
    const getTextInsightsResponse = (await request(requestUrl, {
      method: "GET",
    })) as TeamScanFeedbackResponse;

    const filteredResponse = {
      doingWell: getTextInsightsResponse.doingWell.filter(
        (feedback) => feedback.response.trim() !== ""
      ),
      couldImproveOn: getTextInsightsResponse.couldImproveOn.filter(
        (feedback) => feedback.response.trim() !== ""
      ),
    };

    return filteredResponse;
  }
);

export const getFeedbackResponsesCount = createAsyncThunk(
  "adminConsole/getFeedbackResponsesCount",
  async (payload: undefined | OpenAIUrlParams) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/feedbackResponsesCount${getAppendedOpenAIUrl(
      {
        ...payload,
      }
    )}`;
    const getFeedbackResponsesCountResponse = (await request(requestUrl)) as {
      numberOfFeedbackResponses: number;
    };
    return getFeedbackResponsesCountResponse.numberOfFeedbackResponses;
  }
);

export const getTeamAndDepartmentLeads = createAsyncThunk(
  "adminConsole/getTeamAndDepartmentLeads",
  async (_: { forceFetch: boolean } | undefined, { getState }) => {
    const {
      adminConsole: { isDepartmentsHidden },
    } = getState() as RootState;

    const requestUrl = `${REACT_APP_API_URL}/talentInsights/teamAndDepartmentLeads`;
    const getTeamAndDepartmentLeadsResponse = (await request(
      requestUrl
    )) as TeamAndDepartmentLeadsResponse;

    if (isDepartmentsHidden) {
      const { users } = getTeamAndDepartmentLeadsResponse;
      Object.keys(users).forEach((userAccountId) => {
        getTeamAndDepartmentLeadsResponse.users[
          Number(userAccountId)
        ].departments = undefined;
      });
    }

    return getTeamAndDepartmentLeadsResponse;
  },
  {
    condition: (options: { forceFetch: boolean } | undefined, { getState }) => {
      if (options?.forceFetch) return true;
      const {
        adminConsole: { getTeamAndDepartmentLeadsCalled },
      } = getState() as RootState;
      return !getTeamAndDepartmentLeadsCalled;
    },
  }
);

export const reInviteUser = createAsyncThunk(
  "adminConsole/reInviteUser",
  async (userAccountId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${userAccountId}/invite`;
    const reInviteUserResponse = await request(requestUrl);
    return reInviteUserResponse;
  }
);

export const getTeamAssessmentInstances = createAsyncThunk(
  "adminConsole/getTeamAssessmentInstances",
  async (_: { forceRefresh: boolean } | undefined) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/teamAssessmentsInstances`;
    const response = (await request(requestUrl)) as {
      [assessmentInstanceId: number]: TeamAssessmentInstance;
    };

    const responseArray: TeamAssessmentInstance[] = [];
    Object.entries(response).forEach(([assessmentInstanceId, instance]) => {
      responseArray.push({
        ...instance,
        assessmentInstanceId: Number(assessmentInstanceId),
      });
    });
    return responseArray;
  },
  {
    condition: (
      payload: { forceRefresh: boolean } | undefined,
      { getState }
    ) => {
      const {
        adminConsole: { organizationTeamAssessmentInstances },
      } = getState() as RootState;
      if (payload?.forceRefresh) return true;
      if (organizationTeamAssessmentInstances.length) {
        return false;
      }
    },
  }
);

export const getAssessmentInformation = createAsyncThunk(
  "adminConsole/getAssessmentInformation",
  async (_: { forceRefresh: boolean } | undefined) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/assessmentInformation`;
    const response = (await request(requestUrl)) as AssessmentInformation;
    return response;
  },
  {
    condition: (
      payload: { forceRefresh: boolean } | undefined,
      { getState }
    ) => {
      const {
        adminConsole: {
          assessmentInformation,
          gettingAssessmentInformationStatus,
        },
      } = getState() as RootState;
      if (payload?.forceRefresh) return true;
      if (
        assessmentInformation ||
        gettingAssessmentInformationStatus === "loading"
      ) {
        return false;
      }
    },
  }
);

export const inviteCreatedUsers = createAsyncThunk(
  "adminConsole/inviteCreatedUsers",
  async (_, { getState, dispatch }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/inviteCreatedUsers`;
    const inviteCreatedUsersResponse = await request(requestUrl);
    const {
      global: { createdButNotInvitedUsersIds },
    } = getState() as RootState;
    dispatch(
      removeUsersFromCreatedButNotInvitedUsersIds(createdButNotInvitedUsersIds)
    );
    const updatedUserObjects: { [id: number]: { tmgInvited: 1 } } = {};
    createdButNotInvitedUsersIds.forEach((id) => {
      updatedUserObjects[id] = { tmgInvited: 1 };
    });
    dispatch(updateUsersAction(updatedUserObjects));

    return inviteCreatedUsersResponse;
  }
);

export const inviteCreatedUser = createAsyncThunk(
  "adminConsole/inviteCreatedUser",
  async (userAccountId: number, { dispatch }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/inviteCreatedUser/${userAccountId}`;
    const inviteCreatedUserResponse = await request(requestUrl);
    dispatch(removeUsersFromCreatedButNotInvitedUsersIds([userAccountId]));
    dispatch(updateUsersAction({ [userAccountId]: { tmgInvited: 1 } }));
    return inviteCreatedUserResponse;
  }
);

export const inviteTeamLeadsForTeamId = createAsyncThunk(
  "adminConsole/inviteTeamLeadsForTeamId",
  async (teamId: number, { dispatch }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/teams/${teamId}/inviteTeamLeads`;
    const inviteTeamLeadsForTeamIdResponse = await request(requestUrl);
    dispatch(updateTeamsAction({ [teamId]: { uninvitedTeamLeaders: [] } }));
    return inviteTeamLeadsForTeamIdResponse;
  }
);

export const getVisibilityPermissionDifferCount = createAsyncThunk(
  "adminConsole/getVisibilityPermissionDifferCount",
  async (visibilityPermissionDefaultId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/visibilityPermissionDefaults/${visibilityPermissionDefaultId}/differingCount`;
    const response = (await request(
      requestUrl
    )) as PermissionDefaultUpdatedEntities;
    return {
      visibilityPermissionDefaultId,
      response,
    };
  }
);

export const getMatches = createAsyncThunk(
  "adminConsole/getMatches",
  async (query: string) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/testTakers?searchString=${query}`;
    const response = (await request(requestUrl)) as {
      [key: string]: TestTakerMatch;
    };

    let matches: TestTakerMatch[] = [];
    try {
      matches = Object.values(response);
    } catch (e) {
      matches = [];
    }
    return { query, response: matches };
  },
  {
    condition: (query, { getState }) => {
      const {
        adminConsole: { testTakeQueriedMatches },
      } = getState() as RootState;
      if (testTakeQueriedMatches[query]) {
        return false;
      }
    },
  }
);

export const getTeamMembersWithPendingTeamScanSurvey = createAsyncThunk(
  "adminConsole/getTeamMembersWithPendingTeamScanSurvey",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/usersWithPendingActiveTEAMscans`;
    const response = (await request(requestUrl)) as { userAccountId: number }[];
    return response;
  }
);

export const getPendingTeamMemberships = createAsyncThunk(
  "adminConsole/getPendingTeamMemberships",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/pendingTeamMemberships`;
    const response = (await request(requestUrl)) as PendingTeamMember[];
    return response;
  }
);

export const getAllDeactivatedUsers = createAsyncThunk(
  "global/getAllDeactivatedUsers",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/deactivatedUsers`;
    const getAllDeactivatedUsersResponse = (await request(
      requestUrl
    )) as UserInfo[];
    return getAllDeactivatedUsersResponse;
  },
  {
    condition: (_, { getState }) => {
      const {
        adminConsole: { getAllDeactivatedUsersStatus },
      } = getState() as RootState;
      if (getAllDeactivatedUsersStatus === "idle") return true;
    },
  }
);

// This endpoint will be used for checking the email when editing a user
export const checkUserEmail = createAsyncThunk(
  "adminConsole/checkUserEmail",
  async ({
    emailAddress,
    userAccountId,
  }: {
    emailAddress: string;
    userAccountId?: number;
  }) => {
    const params: Record<string, string> = {
      emailAddress,
    };

    if (userAccountId) {
      params.userAccountId = userAccountId.toString();
    }

    const queryString = new URLSearchParams(params).toString();
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/checkUserEmail?${queryString}`;

    const response = (await request(
      requestUrl,
      {
        method: "GET",
      },
      true
    )) as CheckSingleEmailResponse & {
      status: number;
    };

    return {
      isValid: response.isValid,
      error: response.error,
      userAccountId,
      emailAddress,
    };
  }
);

export const getOverallActivityTableData = createAsyncThunk(
  "adminConsole/getOverallActivityTableData",
  async (
    { timeInterval, entityType, companyAccountId }: ActivityTablePayload,
    { getState }
  ) => {
    const companyKey = getActivityCompanyKey(getState() as RootState, companyAccountId);
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/overallActivityTableData/${timeInterval}/${entityType}${getActivityCompanyQuery(companyKey)}`;
    const response = (await request(requestUrl)) as {
      [key: string]: OverallActivityTableRow;
    };
    const responseArray = Object.values(response);

    return { response: responseArray, timeInterval, entityType, companyKey };
  },
  {
    condition: ({ timeInterval, entityType, companyAccountId }, { getState }) => {
      const state = getState() as RootState;
      const {
        getOverallActivityTableDataStatus,
        allOverallActivityTableData,
        overallActivityTableDataOwner,
      } = state.adminConsole;
      return shouldFetchActivityData(
        overallActivityTableDataOwner,
        getActivityCompanyKey(state, companyAccountId),
        getOverallActivityTableDataStatus,
        !!allOverallActivityTableData[timeInterval]?.[entityType]
      );
    },
    getPendingMeta: ({ arg }, { getState }) => ({
      companyKey: getActivityCompanyKey(getState() as RootState, arg.companyAccountId),
    }),
  }
);

// ------------------------------------ ProviderIntegration ------------------------------------

export const connectIntegration = createAsyncThunk(
  "adminConsole/connectIntegration",
  async (provider: string, { rejectWithValue }) => {
    try {
      const frontendOrigin = window.location.origin;

      const requestUrl = `${REACT_APP_API_URL}/talentInsights/${provider}/redirect?frontend_origin=${encodeURIComponent(
        frontendOrigin
      )}`;

      const response = (await request(requestUrl, { method: "GET" }, true)) as {
        redirectUrl: string;
      };

      if (!response.redirectUrl) {
        throw new Error(`Missing redirect URL for provider: ${provider}`);
      }

      return response.redirectUrl;
    } catch (err: any) {
      console.error(`connectIntegration(${provider}) failed:`, err);
      return rejectWithValue(
        err?.message || `Failed to get redirect URL for provider: ${provider}`
      );
    }
  }
);

export const fetchIntegrationStatus = createAsyncThunk(
  "adminConsole/fetchIntegrationStatus",
  async (provider: string, { rejectWithValue }) => {
    try {
      const requestUrl = `${REACT_APP_API_URL}/talentInsights/${provider}/status`;

      const response = (await request(requestUrl, { method: "GET" }, true)) as {
        hasIntegration?: boolean;
      };
      return {
        provider,
        hasIntegration: Boolean(response?.hasIntegration),
        error: false,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.message || `Failed to fetch integration status for ${provider}`
      );
    }
  },
  {
    // Only allow one in-flight request per provider at a time
    condition: (provider, { getState }) => {
      const {
        adminConsole: { fetchIntegrationStatusStatus },
      } = getState() as RootState;
      if (fetchIntegrationStatusStatus[provider] === "loading") {
        return false;
      }
    },
  }
);

interface IntegrationsDisconnectResponse {
  status: boolean;
  message?: string;
}

export const disconnectIntegration = createAsyncThunk<
  IntegrationsDisconnectResponse, // Return type
  string, // Argument type (provider)
  { rejectValue: string } // Rejection type
>(
  "adminConsole/disconnectIntegration",
  async (provider, { rejectWithValue }) => {
    try {
      const requestUrl = `${REACT_APP_API_URL}/talentInsights/${provider}/disconnect`;

      const response = await request(requestUrl, { method: "POST" }, true);

      const data = response as IntegrationsDisconnectResponse;

      if (!data.status) {
        throw new Error(
          data.message || `Failed to disconnect ${provider} integration`
        );
      }

      return data;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || `Failed to disconnect ${provider}`
      );
    }
  }
);

export const sendTestMessage = createAsyncThunk(
  "adminConsole/sendTeamsTestMessage",
  async (provider: string, { rejectWithValue }) => {
    try {
      const requestUrl = `${REACT_APP_API_URL}/talentInsights/${provider}/send-test-message`;

      const response = (await request(
        requestUrl,
        { method: "POST" },
        true
      )) as TeamsTestMessageResponse;

      if (!response.success) {
        return rejectWithValue(response.error || "Failed to send test message");
      }

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to send test message");
    }
  }
);

// ------------------------------------ POST Requests ------------------------------------

export const scheduleTEAM360 = createAsyncThunk(
  "adminConsole/scheduleTEAM360",
  async (payload: ScheduleSurveyPayload, { dispatch }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/scheduleTEAM360`;
    const scheduleTEAM360Response = await request(requestUrl, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    // If a departmentId is included then we should fire the getDepartmentInsightReport action again to get the updated data
    if (payload.departmentIds?.length) {
      const departmentId = payload.departmentIds[0];
      dispatch(getDepartmentInsightReport({ departmentId }));
      dispatch(getDepartmentResultsInfo(departmentId));
    }

    if (payload.teamIds?.length) {
      const teamId = payload.teamIds[0];
      dispatch(getTeamDisplayStatusByTeamId({ teamId }));
      dispatch(getTeamAnalyticsText({ teamId }));
      dispatch(
        getTeamAssessmentInstancesByTeamId({
          teamId,
          forceRefresh: true,
        })
      );
      dispatch(onLaunchTeamScanForTeamIdsDismissNotification(payload.teamIds));
    }

    payload.teamIds?.forEach((teamId) => {
      dispatch(setTeamScanAssessmentActiveStatus({ teamId, isActive: true }));
    });
    dispatch(getNotifications());
    dispatch(getMostRecentAssessmentInstances({ forceRefresh: true }));
    dispatch(getAssessmentInstances({ forceRefresh: true }));
    return scheduleTEAM360Response;
  }
);

export const createDepartment = createAsyncThunk(
  "adminConsole/createDepartment",
  async (
    payload: {
      name?: string;
      leader?: number | null; // null means no leader, undefined means no change
      departmentTypeId?: number;
      departmentLeaderEmail?: string;
      hasCoachBoWeeklyCheckInsEnabled?: boolean | 0 | 1;
      sendDepartmentLeaderInvitationEmail?: boolean;
    },
    { dispatch }
  ) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departments`;
    const { departmentId } = (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify(payload),
    })) as { departmentId: number };
    dispatch(onSuccessfulDepartmentCreation(departmentId));
    return {
      name: payload.name,
      leader: payload.leader,
      departmentTypeId: payload.departmentTypeId,
      departmentLeaderEmail: payload.departmentLeaderEmail,
      departmentId,
    };
  }
);

export const createCompanyInviteLink = createAsyncThunk(
  "adminConsole/createCompanyInviteLink",
  async ({
    payload,
    companyAccountId,
  }: {
    payload: { expiration: number };
    companyAccountId: number;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/companies/${companyAccountId}/invitationLink`;
    const { link } = (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify(payload),
    })) as { link: string };
    return link;
  },
  {
    // The link is created once per session, when a modal that shows it is opened
    condition: (_, { getState }) => {
      const {
        adminConsole: { createCompanyInviteLinkStatus },
      } = getState() as RootState;
      if (
        createCompanyInviteLinkStatus === "loading" ||
        createCompanyInviteLinkStatus === "succeeded"
      ) {
        return false;
      }
    },
  }
);

// This is used to check new emails(returns back error if user does not have access to add email to add new users)
export const checkNewEmail = createAsyncThunk(
  "adminConsole/checkNewEmail",
  async (emailAddress: string) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/checkNewEmail`;
    const response = (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({ emailAddress }),
    })) as CheckSingleEmailResponse;
    return { response, emailAddress };
  },
  {
    condition: (emailAddress, { getState }) => {
      const {
        adminConsole: { newCheckedEmails },
      } = getState() as RootState;
      if (newCheckedEmails[emailAddress]) {
        return false;
      }
    },
  }
);

export const inviteTeamMembersWithPendingTeamScanSurvey = createAsyncThunk(
  "adminConsole/inviteTeamMembersWithPendingTeamScanSurvey",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/sendInvitationToUsersWithPendingActiveTEAMscans`;
    const response = await request(requestUrl, {
      method: "POST",
    });
    return response;
  }
);

export const getTEAMscanActivityTableData = createAsyncThunk(
  "adminConsole/getTEAMscanActivityTableData",
  async (
    { timeFrame, companyAccountId }: GetTEAMscanActivityTablePayload,
    { getState }
  ) => {
    const companyKey = getActivityCompanyKey(getState() as RootState, companyAccountId);
    const params: Record<string, string> = { timeFrame };
    if (companyKey) params.companySelector = companyKey;
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/TEAMscanActivityTable?${new URLSearchParams(params)}`;
    const response = (await request(requestUrl)) as TimeIntervalTEAMscanActivityTableData;

    const copy: TimeIntervalTEAMscanActivityTableData = {};
    Object.entries(response).forEach(([key, value]) => {
      copy[key] = value?.sort(
        (a, b) =>
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
      );
    });

    return { response: copy, timeFrame, companyKey };
  },
  {
    condition: ({ timeFrame, companyAccountId }, { getState }) => {
      const state = getState() as RootState;
      const {
        getTEAMscanActivityTableDataStatus,
        allTEAMscanActivityTableData,
        TEAMscanActivityTableDataOwner,
      } = state.adminConsole;
      return shouldFetchActivityData(
        TEAMscanActivityTableDataOwner,
        getActivityCompanyKey(state, companyAccountId),
        getTEAMscanActivityTableDataStatus,
        !!allTEAMscanActivityTableData[timeFrame]
      );
    },
    getPendingMeta: ({ arg }, { getState }) => ({
      companyKey: getActivityCompanyKey(getState() as RootState, arg.companyAccountId),
    }),
  }
);

export const getEppActivityTableData = createAsyncThunk(
  "adminConsole/getEppActivityTableData",
  async (
    { entityType, companyAccountId }: GetEppActivityTablePayload,
    { getState }
  ) => {
    const companyKey = getActivityCompanyKey(getState() as RootState, companyAccountId);
    const params: Record<string, string> = { timeFrame: "allTime", entityType };
    if (companyKey) params.companySelector = companyKey;
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/EppActivityTable?${new URLSearchParams(params)}`;
    const response = (await request(requestUrl)) as EppActivityTableData;

    return { response, entityType, companyKey };
  },
  {
    condition: ({ entityType, companyAccountId }, { getState }) => {
      const state = getState() as RootState;
      const {
        getEppActivityTableDataStatus,
        allEppActivityTableData,
        eppActivityTableDataOwner,
      } = state.adminConsole;
      return shouldFetchActivityData(
        eppActivityTableDataOwner,
        getActivityCompanyKey(state, companyAccountId),
        getEppActivityTableDataStatus,
        !!allEppActivityTableData[entityType]
      );
    },
    getPendingMeta: ({ arg }, { getState }) => ({
      companyKey: getActivityCompanyKey(getState() as RootState, arg.companyAccountId),
    }),
  }
);

// ------------------------------------ PUT Requests ------------------------------------

export const updateUserEmail = createAsyncThunk(
  "adminConsole/updateUserEmail",
  async (
    {
      userAccountId,
      emailAddress,
    }: {
      userAccountId: number;
      emailAddress: string;
    },
    { dispatch }
  ) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/updateUserEmail`;
    const response = await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({ emailAddress, userAccountId }),
    });

    dispatch(updateUsersAction({ [userAccountId]: { emailAddress } }));

    return response;
  }
);

export const updateCompanySettings = createAsyncThunk(
  "adminConsole/updateCompanySettings",
  async ({
    payload,
    companyAccountId,
  }: {
    payload: CompanySettingPayload;
    companyAccountId: number;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/companies/${companyAccountId}/settings`;
    const response = (await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
    })) as { settings: CompanySettings };
    const settings = { ...response?.settings, ...payload };
    return settings;
  }
);

export const updateDepartment = createAsyncThunk(
  "adminConsole/updateDepartment",
  async (
    payload: Partial<{
      departmentId: number;
      name?: string;
      leader?: number | null; // null means no leader, undefined means no change
      departmentTypeId?: number;
      departmentLeaderEmail?: string;
      hasCoachBoWeeklyCheckInsEnabled?: boolean | 0 | 1;
      sendDepartmentLeaderInvitationEmail?: boolean;
    }>,
    { getState, dispatch }
  ) => {
    const { departmentId, leader } = payload;
    if (!departmentId) {
      throw new Error("Department ID is required to update a department");
    }

    const {
      adminConsole: { departments },
    } = getState() as RootState;

    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departments/${departmentId}`;
    const updateDepartmentResponse = (await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
    })) as Department;

    // If the old department information had a department leader then we need to dispatch the action to remove the departmentId from the user's departmentIds array
    const previousDepartmentLeaderId = getDepartmentLeadId(
      departmentId,
      departments
    );

    if (previousDepartmentLeaderId) {
      dispatch(
        removeUserLeader({
          type: "departments",
          id: departmentId,
          userAccountId: previousDepartmentLeaderId,
        })
      );
    }

    // If the new department information has a department leader then we need to dispatch the action to add the departmentId to the user's departmentIds array
    if (typeof leader === "number") {
      dispatch(
        addUserLeader({
          type: "departments",
          id: departmentId,
          userAccountId: leader,
        })
      );
    }

    dispatch(onSuccessfulDepartmentCreation(departmentId));

    return updateDepartmentResponse;
  }
);

export const reassignTeamLead = createAsyncThunk(
  "adminConsole/reassignTeamLead",
  async ({
    newTeamLead,
    currentTeamLead,
    teamId,
  }: {
    newTeamLead: number;
    currentTeamLead: number;
    teamId: number;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/teams/${teamId}/teamLead`;
    const reassignTeamLeadResponse = await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({
        newTeamLead,
        currentTeamLead,
      }),
    });
    return reassignTeamLeadResponse;
  }
);

export const updateTeam360 = createAsyncThunk(
  "adminConsole/updateTeam360",
  async (
    { teamId, endDate }: { teamId: number; endDate?: string },
    { dispatch }
  ) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/teams/${teamId}/updateTeam360`;
    const response = await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({ endDate }),
    });
    dispatch(getTeamAnalyticsText({ teamId }));
    return response;
  }
);

export const updateAssessmentSeries = createAsyncThunk(
  "adminConsole/updateAssessmentSeries",
  async ({
    payload,
    assessmentId,
  }: {
    payload: PutAssessmentPayload & { stopRecurringDate?: string };
    assessmentId: number;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/assessments/${assessmentId}`;
    const response = await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({ ...payload }),
    });
    return response;
  }
);

export const updateAssessmentInstance = createAsyncThunk(
  "adminConsole/updateAssessmentInstance",
  async ({
    payload,
    assessmentId,
  }: {
    payload: PutAssessmentPayload;
    assessmentId: number;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/assessmentInstances/${assessmentId}`;
    const response = await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({ ...payload }),
    });
    return response;
  }
);

export const linkTestTaker = createAsyncThunk(
  "adminConsole/linkTestTaker",
  async ({
    userAccountId,
    testTakerId,
  }: {
    userAccountId: number;
    testTakerId: number;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${userAccountId}/linkTestTaker/${testTakerId}`;
    const response = await request(requestUrl, {
      method: "PUT",
    });
    return response;
  }
);

export const updateVisibilitySettings = createAsyncThunk(
  "adminConsole/updateVisibilitySettings",
  async (
    { payload }: { payload: CompanyVisibilitySettingPayload },
    { dispatch }
  ) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/visibilitySettings`;
    (await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
    })) as CompanyVisibilitySettingPayload;

    // When the company visibility settings are updated we need to clear all the user/team visibility settings so that the new settings can be fetched
    dispatch(resetUserVisibilitySettings());
    dispatch(clearTeamVisibilitySettings());
    return payload;
  }
);

export const bulkUpdatePendingTeamMemberships = createAsyncThunk(
  "adminConsole/bulkUpdatePendingTeamMemberships",
  async (payload: { action: "accept" | "decline" }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/bulkUpdatePendingTeamMemberships`;
    const response = await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    return response;
  }
);

export const reactivateUser = createAsyncThunk(
  "adminConsole/reactivateUser",
  async (userAccountId: number, { dispatch }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/reactivateUser/${userAccountId}`;
    await request(requestUrl, {
      method: "PUT",
    });
    // once reactivating the user we should remove the user from the deactivatedUsers state and make a call to getUserInfoById to add it to the users state
    dispatch(getUserInfoById({ userAccountId, forceRefresh: true }));

    return userAccountId;
  }
);

export const updateCompanyTimezone = createAsyncThunk(
  "adminConsole/updateCompanyTimezone",
  async (timezone: string) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/companyTimezone`;
    await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({ timezone }),
    });
    toast.success("Time zone updated successfully", {
      ...successNotificationOptions,
    });
    return timezone;
  }
);

export const deactivateAutomaticTeam = createAsyncThunk(
  "adminConsole/deactivateAutomaticTeam",
  async (
    {
      teamId,
      newManagerUserAccountId,
    }: { teamId: number; newManagerUserAccountId?: number | null },
    { getState, rejectWithValue, dispatch }
  ) => {
    const {
      global: { teamsByTeamId, companyInfo },
    } = getState() as RootState;
    const teamInfo = teamsByTeamId[teamId];
    if (!teamInfo || !companyInfo) {
      return rejectWithValue("Team not found or company info not found");
    }

    const managerChanges: UpdateOrganizationalStructurePayload["managerChanges"] =
      [];
    const managerUserAccountId = teamInfo.teamLeadUserAccountIds?.[0];

    teamInfo.teamMemberIds?.forEach((memberId) => {
      if (memberId === managerUserAccountId) {
        return;
      }

      managerChanges.push({
        employeeUserAccountId: memberId,
        newManagerUserAccountId,
      });
    });

    const requestUrl = `${REACT_APP_API_URL}/talentInsights/organizationalChanges`;
    const response = (await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({ managerChanges }),
    })) as UpdateOrganizationalStructureResponse;

    dispatch(
      getAllTeamsByCompanyAccountId({
        companyAccountId: companyInfo.companyAccountId,
        refreshTeams: true,
      })
    );

    return response;
  }
);

// ------------------------------------ DELETE Requests ------------------------------------
export const deleteDepartmentById = createAsyncThunk(
  "adminConsole/deleteDepartments",
  async (departmentId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departments/${departmentId}`;
    await request(requestUrl, {
      method: "DELETE",
    });
    return departmentId;
  }
);

function getTextInsightsError(
  response: TextInsightResponse & { status?: number }
) {
  if (response?.status === 402) {
    return "Your account doesn't have any remaining OpenAI credits, please contact your account manager if you'd like to add more.";
  } else if (response?.status === 500) {
    return "Unable to generate a response, please try again when more responses have been received.";
  }
  return null;
}

// ------------------ Beginning of Slice Definition ------------------
export const adminConsoleSlice = createSlice({
  name: "adminConsole",
  initialState,
  reducers: {
    clearScheduleTEAM360Status: (state) => {
      state.scheduleTEAM360Status = "idle";
    },
    removeUserLeader: (
      state,
      {
        payload: { type, id, userAccountId },
      }: PayloadAction<{
        type: "teams" | "departments";
        id: number;
        userAccountId: number;
      }>
    ) => {
      // If the state has not yet been initialized, return
      if (!state.teamDepartmentLeads) return;

      // If the id is not included then we add it to our state. otherwise do not add it
      const idArray = state.teamDepartmentLeads.users[userAccountId]?.[type];
      if (idArray?.includes(id)) {
        return {
          ...state,
          teamDepartmentLeads: {
            ...state.teamDepartmentLeads,
            users: {
              ...state.teamDepartmentLeads.users,
              [userAccountId]: {
                ...state.teamDepartmentLeads.users[userAccountId],
                [type]: idArray.filter((idx) => idx !== id),
              },
            },
          },
        };
      }
    },
    addUserLeader: (
      state,
      {
        payload: { type, id, userAccountId },
      }: PayloadAction<{
        type: "teams" | "departments";
        id: number;
        userAccountId: number;
      }>
    ) => {
      // If the state has not yet been initialized, return
      if (!state.teamDepartmentLeads) return;

      // If the user does not exist in the state, initialize it and add the id to the state.
      if (!state.teamDepartmentLeads.users[userAccountId]) {
        return {
          ...state,
          teamDepartmentLeads: {
            ...state.teamDepartmentLeads,
            users: {
              ...state.teamDepartmentLeads.users,
              [userAccountId]: {
                // @ts-ignore
                ...state.teamDepartmentLeads.users[userAccountId],
                [type]: [id],
                [type === "teams" ? "departments" : "teams"]: [],
              },
            },
          },
        };
      }

      const idArray =
        state.teamDepartmentLeads.users[userAccountId]?.[type] ?? [];
      // If the id already is part of the users teams or departments, do not add it
      if (idArray.includes(id)) {
        return state;
      } else {
        return {
          ...state,
          teamDepartmentLeads: {
            ...state.teamDepartmentLeads,
            users: {
              ...state.teamDepartmentLeads.users,
              [userAccountId]: {
                ...state.teamDepartmentLeads.users[userAccountId],
                [type]: [...idArray, id],
              },
            },
          },
        };
      }
    },
    resetUpdateTeam360DateRangeStatus(state) {
      state.updatingTeam360DateRangeStatus = "idle";
    },
    setTeam360CardsExpanded: (
      state,
      {
        payload: { value, cardTitle },
      }: PayloadAction<{ value: boolean; cardTitle: string }>
    ) => {
      return {
        ...state,
        team360CardsExpanded: {
          ...state.team360CardsExpanded,
          [cardTitle]: value,
        },
      };
    },
    clearTeam360ExpandedCards: (state) => {
      return {
        ...state,
        team360CardsExpanded: {},
      };
    },
    resetTextSynthesis(state) {
      state.textSynthesis = null;
      state.getTextSynthesisStatus = "idle";
    },
    resetTextInsights(state) {
      state.textInsights = null;
      state.getTextInsightsStatus = "idle";
    },
    resetTeamScanFeedback(state) {
      state.teamScanFeedback = null;
    },
    clearCreatedDepartmentId(state) {
      state.createdDepartmentId = null;
    },
    setTeamScanAssessmentActiveStatus: (
      state,
      {
        payload: { isActive, teamId },
      }: PayloadAction<{
        isActive: boolean;
        teamId: number;
      }>
    ) => {
      if (!state.assessmentInformation) return state;
      const teamsObj = state.assessmentInformation.teams ?? {};
      const team = teamsObj[teamId];
      return {
        ...state,
        assessmentInformation: {
          ...state.assessmentInformation,
          teams: {
            ...teamsObj,
            [teamId]: {
              ...team,
              activeAssessment: isActive,
            },
          },
        },
      };
    },
    toggleDisableDepartments: (state, { payload }: PayloadAction<boolean>) => {
      state.isDepartmentsHidden = payload;
    },
    toggleAIFeaturesEnabled: (state, { payload }: PayloadAction<boolean>) => {
      state.aiFeaturesEnabled = payload;
    },
    setInviteModalOpen: (
      state,
      {
        payload,
      }: PayloadAction<
        | {
            defaultTab?: "csv" | "link" | "email";
            isOpen: boolean;
            teamId?: number;
          }
        | false
      >
    ) => {
      if (payload === false || payload.isOpen === false) {
        state.isInviteModalOpen = null;
        return;
      }
      state.isInviteModalOpen = payload;
    },
    toggleHasCoachBoConversationHistoryEnabled: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.hasCoachBoConversationHistoryEnabled = payload;
    },
    updateCompanySettingsAction: (
      state,
      { payload }: PayloadAction<CompanySettingPayload>
    ) => {
      if (!state.companySettings) return;
      state.companySettings = {
        ...state.companySettings,
        ...payload,
      };
    },
    onHideSurveyTableBanner: (state) => {
      state.hideSurveyTableBanner = true;
    },
    clearPendingTeamMemberships: (state) => {
      state.pendingTeamMemberships = [];
    },
    resetEditedCheckedEmails: (state) => {
      state.checkedEditedEmails = {};
    },
    updateDepartmentsBasedOnWeeklyCheckInCustomList: (
      state,
      { payload }: PayloadAction<{ remove: number[]; add: number[] }>
    ) => {
      state.departments = updateEntitiesBasedOnChangesToCustomList(
        state.departments,
        payload
      );
    },
    resetCreateDepartmentStatus: (state) => {
      state.createDepartmentStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanySettings.pending, (state) => {
        state.getCompanySettingsStatus = "loading";
      })
      .addCase(
        getCompanySettings.fulfilled,
        (state, action: PayloadAction<CompanySettings>) => {
          return {
            ...state,
            getCompanySettingsStatus: "succeeded",
            companySettings: action.payload,
            aiFeaturesEnabled: !!action.payload.aiFeaturesEnabled,
            hasCoachBoConversationHistoryEnabled:
              !!action.payload.hasCoachBoConversationHistoryEnabled,
          };
        }
      )
      .addCase(getCompanySettings.rejected, (state) => {
        state.getCompanySettingsStatus = "failed";
      })
      .addCase(updateCompanySettings.pending, (state) => {
        state.updateCompanySettingsStatus = "loading";
      })
      .addCase(
        updateCompanySettings.fulfilled,
        (state, { payload }: PayloadAction<Partial<CompanySettings>>) => {
          toast.success("Updating Company Settings successfully", {
            ...successNotificationOptions,
          });

          const updatedCompanySettings = {
            ...(state.companySettings ?? {}),
            ...payload,
          } as CompanySettings;

          return {
            ...state,
            updateCompanySettingsStatus: "succeeded",
            companySettings: updatedCompanySettings,
          };
        }
      )
      .addCase(updateCompanySettings.rejected, (state, payload) => {
        state.updateCompanySettingsStatus = "failed";
      })
      .addCase(getDepartmentTypes.pending, (state) => {
        state.getDepartmentTypesStatus = "loading";
      })
      .addCase(
        getDepartmentTypes.fulfilled,
        (state, action: PayloadAction<DepartmentType[]>) => {
          const departmentTypes: {
            [departmentTypeId: number]: DepartmentType;
          } = {};

          action.payload.forEach((departmentType) => {
            departmentTypes[departmentType.departmentTypeId] = departmentType;
          });

          return {
            ...state,
            getDepartmentTypesStatus: "succeeded",
            departmentTypes,
          };
        }
      )
      .addCase(getDepartmentTypes.rejected, (state) => {
        state.getDepartmentTypesStatus = "failed";
      })
      .addCase(getDepartments.pending, (state) => {
        state.getDepartmentsStatus = "loading";
      })
      .addCase(
        getDepartments.fulfilled,
        (
          state,
          { payload }: PayloadAction<{ [departmentId: number]: Department }>
        ) => {
          return {
            ...state,
            getDepartmentsStatus: "succeeded",
            departments: { ...payload },
          };
        }
      )
      .addCase(getDepartments.rejected, (state) => {
        state.getDepartmentsStatus = "failed";
      })
      .addCase(createDepartment.pending, (state) => {
        state.createDepartmentStatus = "loading";
      })
      .addCase(
        createDepartment.fulfilled,
        (state, { payload }: PayloadAction<Department>) => {
          toast.success("Created Department successfully", {
            ...successNotificationOptions,
          });
          return {
            ...state,
            createDepartmentStatus: "succeeded",
            departments: {
              ...state.departments,
              [payload.departmentId]: payload,
            },
            createdDepartmentId: payload.departmentId,
          };
        }
      )
      .addCase(createDepartment.rejected, (state) => {
        state.createDepartmentStatus = "failed";
      })
      .addCase(createCompanyInviteLink.pending, (state) => {
        state.createCompanyInviteLinkStatus = "loading";
      })
      .addCase(createCompanyInviteLink.fulfilled, (state, action) => {
        return {
          ...state,
          createCompanyInviteLinkStatus: "succeeded",
          companyInviteLink: action.payload,
        };
      })
      .addCase(createCompanyInviteLink.rejected, (state) => {
        state.createCompanyInviteLinkStatus = "failed";
      })
      .addCase(updateDepartment.pending, (state) => {
        state.updateDepartmentStatus = "loading";
      })
      .addCase(
        updateDepartment.fulfilled,
        (state, { payload }: PayloadAction<Department>) => {
          toast.success("Department updated successfully", {
            ...successNotificationOptions,
          });
          return {
            ...state,
            updateDepartmentStatus: "succeeded",
            departments: {
              ...state.departments,
              [payload.departmentId]: {
                ...state.departments[payload.departmentId],
                ...payload,
              },
            },
          };
        }
      )
      .addCase(updateDepartment.rejected, (state) => {
        state.updateDepartmentStatus = "failed";
      })
      .addCase(deleteDepartmentById.pending, (state) => {
        state.deleteDepartmentStatus = "loading";
      })
      .addCase(
        deleteDepartmentById.fulfilled,
        (state, { payload }: PayloadAction<number>) => {
          toast.success("Department deleted successfully", {
            ...successNotificationOptions,
          });
          const newDepartments = { ...state.departments };
          delete newDepartments[payload];
          return {
            ...state,
            deleteDepartmentStatus: "succeeded",
            departments: newDepartments,
          };
        }
      )
      .addCase(deleteDepartmentById.rejected, (state) => {
        state.deleteDepartmentStatus = "failed";
      })
      .addCase(getAnalyticsText.pending, (state) => {
        state.getAnalyticsTextStatus = "loading";
      })
      .addCase(
        getAnalyticsText.fulfilled,
        (state, { payload }: PayloadAction<AnalyticsText>) => {
          return {
            ...state,
            getAnalyticsTextStatus: "succeeded",
            analyticsText: payload,
          };
        }
      )
      .addCase(getAnalyticsText.rejected, (state) => {
        state.getAnalyticsTextStatus = "failed";
      })
      .addCase(getSampleAnalyticsText.pending, (state) => {
        state.getSampleAnalyticsTextStatus = "loading";
      })
      .addCase(
        getSampleAnalyticsText.fulfilled,
        (state, { payload }: PayloadAction<AnalyticsText>) => {
          return {
            ...state,
            getSampleAnalyticsTextStatus: "succeeded",
            sampleAnalyticsText: payload,
          };
        }
      )
      .addCase(getSampleAnalyticsText.rejected, (state) => {
        state.getSampleAnalyticsTextStatus = "failed";
      })
      .addCase(getTextSynthesis.pending, (state) => {
        state.getTextSynthesisStatus = "loading";
      })
      .addCase(
        getTextSynthesis.fulfilled,
        (state, { payload }: PayloadAction<TextSynthesisResponse>) => {
          return {
            ...state,
            getTextSynthesisStatus: "succeeded",
            textSynthesis: payload,
          };
        }
      )
      .addCase(getTextSynthesis.rejected, (state) => {
        state.getTextSynthesisStatus = "failed";
      })
      .addCase(getWordCloud.pending, (state) => {
        state.getWordCloudStatus = "loading";
      })
      .addCase(
        getWordCloud.fulfilled,
        (state, { payload }: PayloadAction<WordCloudResponse>) => {
          return {
            ...state,
            getWordCloudStatus: "succeeded",
            wordCloud: payload,
          };
        }
      )
      .addCase(getWordCloud.rejected, (state) => {
        state.getWordCloudStatus = "failed";
      })
      .addCase(getTextInsights.pending, (state) => {
        state.getTextInsightsStatus = "loading";
      })
      .addCase(
        getTextInsights.fulfilled,
        (state, { payload }: PayloadAction<TextInsightResponse>) => {
          return {
            ...state,
            getTextInsightsStatus: "succeeded",
            textInsights: payload,
          };
        }
      )
      .addCase(getTextInsights.rejected, (state, action) => {
        state.getTextInsightsStatus = "failed";
        state.textInsightsErrorMessage = action.error.message as string;
      })
      .addCase(getFeedbackResponsesCount.pending, (state) => {
        state.getFeedbackResponsesCountStatus = "loading";
      })
      .addCase(
        getFeedbackResponsesCount.fulfilled,
        (state, { payload }: PayloadAction<number>) => {
          return {
            ...state,
            getFeedbackResponsesCountStatus: "succeeded",
            feedbackResponsesCount: payload,
          };
        }
      )
      .addCase(getFeedbackResponsesCount.rejected, (state) => {
        state.getFeedbackResponsesCountStatus = "failed";
      })
      .addCase(getGenderInsights.pending, (state) => {
        state.getGenderInsightsStatus = "loading";
      })
      .addCase(
        getGenderInsights.fulfilled,
        (state, { payload }: PayloadAction<GenderInsightsResponse>) => {
          return {
            ...state,
            getGenderInsightsStatus: "succeeded",
            genderInsights: payload,
          };
        }
      )
      .addCase(getGenderInsights.rejected, (state) => {
        state.getGenderInsightsStatus = "failed";
      })
      .addCase(scheduleTEAM360.pending, (state) => {
        state.scheduleTEAM360Status = "loading";
      })
      .addCase(scheduleTEAM360.fulfilled, (state) => {
        toast.success("Scheduling TEAMscan is successful!", {
          ...successNotificationOptions,
        });
        state.scheduleTEAM360Status = "succeeded";
      })
      .addCase(scheduleTEAM360.rejected, (state) => {
        state.scheduleTEAM360Status = "failed";
      })
      .addCase(getTeamAndDepartmentLeads.pending, (state) => {
        state.getTeamAndDepartmentLeadsStatus = "loading";
      })
      .addCase(
        getTeamAndDepartmentLeads.fulfilled,
        (state, { payload }: PayloadAction<TeamAndDepartmentLeadsResponse>) => {
          return {
            ...state,
            getTeamAndDepartmentLeadsStatus: "succeeded",
            getTeamAndDepartmentLeadsCalled: true,
            teamDepartmentLeads: payload,
          };
        }
      )
      .addCase(getTeamAndDepartmentLeads.rejected, (state, action) => {
        if (action.payload === "Already fetched") {
          state.getTeamAndDepartmentLeadsStatus = "succeeded";
        } else {
          state.getTeamAndDepartmentLeadsStatus = "failed";
        }
      })
      .addCase(reInviteUser.fulfilled, () => {
        toast.success("Invitation Sent Successfully!", {
          ...successNotificationOptions,
        });
      })
      .addCase(inviteCreatedUser.fulfilled, () => {
        toast.success("Invitation Sent Successfully!", {
          ...successNotificationOptions,
        });
      })
      .addCase(updateTeam360.fulfilled, (state) => {
        toast.success("TEAMscan Updated Successfully!", {
          ...successNotificationOptions,
        });
      })
      .addCase(updateTeam360DateRange.pending, (state) => {
        state.updatingTeam360DateRangeStatus = "loading";
      })
      .addCase(
        updateTeam360DateRange.fulfilled,
        (
          state,
          {
            payload: { startDate, endDate, instanceType, instance },
          }: PayloadAction<
            {
              instanceType?: (typeof TIME_INTERVAL_OPTIONS)[number];
            } & StartAndEndDateOrInstance
          >
        ) => {
          return {
            ...state,
            updatingTeam360DateRangeStatus: "succeeded",
            team360InstancePicked: {
              instanceType: instanceType ?? null,
              startDate,
              endDate,
              instance,
            },
          };
        }
      )
      .addCase(updateTeam360DateRange.rejected, (state) => {
        state.updatingTeam360DateRangeStatus = "failed";
      })
      .addCase(getTeamAssessmentInstances.pending, (state) => {
        state.getTeamAssessmentInstancesStatus = "loading";
      })
      .addCase(
        getTeamAssessmentInstances.fulfilled,
        (state, action: PayloadAction<TeamAssessmentInstance[]>) => {
          state.getTeamAssessmentInstancesStatus = "succeeded";
          state.organizationTeamAssessmentInstances = action.payload;
        }
      )
      .addCase(getTeamAssessmentInstances.rejected, (state) => {
        state.getTeamAssessmentInstancesStatus = "failed";
      })
      .addCase(getAssessmentInformation.pending, (state) => {
        state.gettingAssessmentInformationStatus = "loading";
      })
      .addCase(
        getAssessmentInformation.fulfilled,
        (state, { payload }: PayloadAction<AssessmentInformation>) => {
          return {
            ...state,
            gettingAssessmentInformationStatus: "succeeded",
            assessmentInformation: payload,
          };
        }
      )
      .addCase(checkNewEmail.pending, (state) => {
        state.checkSingleEmailStatus = "loading";
      })
      .addCase(
        checkNewEmail.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<{
            response: CheckSingleEmailResponse;
            emailAddress: string;
          }>
        ) => {
          return {
            ...state,
            checkSingleEmailStatus: "succeeded",
            newCheckedEmails: {
              ...state.newCheckedEmails,
              [payload.emailAddress]: payload.response,
            },
          };
        }
      )
      .addCase(checkNewEmail.rejected, (state) => {
        state.checkSingleEmailStatus = "failed";
      })
      .addCase(inviteCreatedUsers.pending, (state) => {
        state.inviteCreatedUsersStatus = "loading";
      })
      .addCase(inviteCreatedUsers.fulfilled, (state) => {
        state.inviteCreatedUsersStatus = "succeeded";
        toast.success("Invitations Sent Successfully!", {
          ...successNotificationOptions,
        });
      })
      .addCase(inviteCreatedUsers.rejected, (state) => {
        state.inviteCreatedUsersStatus = "failed";
      })
      .addCase(getMatches.pending, (state) => {
        state.getTestTakerMatchesStatus = "loading";
      })
      .addCase(
        getMatches.fulfilled,
        (state, { payload: { response, query } }) => {
          state.getTestTakerMatchesStatus = "succeeded";
          state.testTakeQueriedMatches = {
            ...state.testTakeQueriedMatches,
            [query]: response,
          };
        }
      )
      .addCase(getMatches.rejected, (state) => {
        state.getTestTakerMatchesStatus = "failed";
      })
      .addCase(updateVisibilitySettings.pending, (state) => {
        state.updateVisibilitySettingsStatus = "loading";
      })
      .addCase(
        updateVisibilitySettings.fulfilled,
        (
          state,
          { payload }: PayloadAction<CompanyVisibilitySettingPayload>
        ) => {
          const { companySettings } = state;
          if (!companySettings) return state;
          toast.success(
            "Visibility Settings Updated Successfully!",
            successNotificationOptions
          );

          return {
            ...state,
            updateVisibilitySettingsStatus: "succeeded",
            companySettings: {
              ...companySettings,
              ...payload,
            },
          };
        }
      )
      .addCase(updateVisibilitySettings.rejected, (state) => {
        state.updateVisibilitySettingsStatus = "failed";
      })
      .addCase(getVisibilityPermissionDifferCount.pending, (state) => {
        state.getVisibilityPermissionDifferCountStatus = "loading";
      })
      .addCase(
        getVisibilityPermissionDifferCount.fulfilled,
        (
          state,
          action: PayloadAction<{
            response: PermissionDefaultUpdatedEntities;
            visibilityPermissionDefaultId: number;
          }>
        ) => {
          return {
            ...state,
            getVisibilityPermissionDifferCountStatus: "succeeded",
            userVisibilityUpdatedEntities: {
              ...state.userVisibilityUpdatedEntities,
              [action.payload.visibilityPermissionDefaultId]:
                action.payload.response,
            },
          };
        }
      )
      .addCase(getVisibilityPermissionDifferCount.rejected, (state) => {
        state.getVisibilityPermissionDifferCountStatus = "failed";
      })
      .addCase(
        getTeamMembersWithPendingTeamScanSurvey.fulfilled,
        (state, { payload }) => {
          state.teamMembersWithPendingTeamScanSurvey = payload;
        }
      )
      .addCase(inviteTeamMembersWithPendingTeamScanSurvey.pending, (state) => {
        state.invitingTeamMembersWithPendingTeamScanSurveyStatus = "loading";
      })
      .addCase(inviteTeamMembersWithPendingTeamScanSurvey.rejected, (state) => {
        state.invitingTeamMembersWithPendingTeamScanSurveyStatus = "failed";
      })
      .addCase(
        inviteTeamMembersWithPendingTeamScanSurvey.fulfilled,
        (state) => {
          toast.success(
            "Invitations Sent Successfully!",
            successNotificationOptions
          );
          state.invitingTeamMembersWithPendingTeamScanSurveyStatus =
            "succeeded";
          state.hideSurveyTableBanner = true;
        }
      )
      .addCase(bulkUpdatePendingTeamMemberships.pending, (state) => {
        state.bulkUpdatePendingTeamMembershipsStatus = "loading";
      })
      .addCase(bulkUpdatePendingTeamMemberships.fulfilled, (state) => {
        state.bulkUpdatePendingTeamMembershipsStatus = "succeeded";
      })
      .addCase(bulkUpdatePendingTeamMemberships.rejected, (state) => {
        state.bulkUpdatePendingTeamMembershipsStatus = "failed";
      })
      .addCase(getPendingTeamMemberships.pending, (state) => {
        state.getPendingTeamMembershipsStatus = "loading";
      })
      .addCase(
        getPendingTeamMemberships.fulfilled,
        (state, { payload }: PayloadAction<PendingTeamMember[]>) => {
          return {
            ...state,
            getPendingTeamMembershipsStatus: "succeeded",
            pendingTeamMemberships: payload,
          };
        }
      )
      .addCase(getPendingTeamMemberships.rejected, (state) => {
        state.getPendingTeamMembershipsStatus = "failed";
      })
      .addCase(getAllDeactivatedUsers.pending, (state) => {
        state.getAllDeactivatedUsersStatus = "loading";
      })
      .addCase(
        getAllDeactivatedUsers.fulfilled,
        (state, { payload }: PayloadAction<UserInfo[]>) => {
          const deactivatedUsers: Record<number, UserInfo> = {};
          payload.forEach((user) => {
            deactivatedUsers[user.userAccountId] = user;
          });
          return {
            ...state,
            getAllDeactivatedUsersStatus: "succeeded",
            deactivatedUsers,
          };
        }
      )
      .addCase(getAllDeactivatedUsers.rejected, (state) => {
        state.getAllDeactivatedUsersStatus = "failed";
      })
      .addCase(reactivateUser.fulfilled, (state, { payload }) => {
        const newDeactivatedUsers = { ...state.deactivatedUsers };
        delete newDeactivatedUsers[payload];
        toast.success("User Reactivated Successfully!", {
          ...successNotificationOptions,
        });
        return {
          ...state,
          deactivatedUsers: newDeactivatedUsers,
        };
      })
      .addCase(checkUserEmail.pending, (state) => {
        state.checkUserEmailStatus = "loading";
      })
      .addCase(checkUserEmail.fulfilled, (state, { payload }) => {
        return {
          ...state,
          checkUserEmailStatus: "succeeded",
          checkedEditedEmails: {
            ...state.checkedEditedEmails,
            [payload.emailAddress]: {
              isValid: payload.isValid,
              error: payload.error,
            },
          },
        };
      })
      .addCase(getOverallActivityTableData.pending, (state, { meta }) => {
        state.getOverallActivityTableDataStatus = "loading";
        if (startActivityRequest(state.overallActivityTableDataOwner, meta)) {
          state.allOverallActivityTableData = {};
        }
      })
      .addCase(
        getOverallActivityTableData.fulfilled,
        (
          state,
          {
            payload: { response, timeInterval, entityType, companyKey },
          }: PayloadAction<{
            response: OverallActivityTableRow[];
            timeInterval: string;
            entityType: string;
            companyKey: string;
          }>
        ) => {
          if (companyKey !== state.overallActivityTableDataOwner.companyKey) return;
          return {
            ...state,
            getOverallActivityTableDataStatus: "succeeded",
            allOverallActivityTableData: {
              ...state.allOverallActivityTableData,
              [timeInterval]: {
                ...state.allOverallActivityTableData[timeInterval],
                [entityType]: response,
              },
            },
          };
        }
      )
      .addCase(getOverallActivityTableData.rejected, (state, { meta }) => {
        if (meta.requestId !== state.overallActivityTableDataOwner.requestId) return;
        state.getOverallActivityTableDataStatus = "failed";
      })
      .addCase(getTEAMscanActivityTableData.pending, (state, { meta }) => {
        state.getTEAMscanActivityTableDataStatus = "loading";
        if (startActivityRequest(state.TEAMscanActivityTableDataOwner, meta)) {
          state.allTEAMscanActivityTableData = {};
        }
      })
      .addCase(getTEAMscanActivityTableData.rejected, (state, { meta }) => {
        if (meta.requestId !== state.TEAMscanActivityTableDataOwner.requestId) return;
        state.getTEAMscanActivityTableDataStatus = "failed";
      })
      .addCase(
        getTEAMscanActivityTableData.fulfilled,
        (
          state,
          {
            payload: { response, timeFrame, companyKey },
          }: PayloadAction<{
            response: TimeIntervalTEAMscanActivityTableData;
            timeFrame: string;
            companyKey: string;
          }>
        ) => {
          if (companyKey !== state.TEAMscanActivityTableDataOwner.companyKey) return;
          return {
            ...state,
            getTEAMscanActivityTableDataStatus: "succeeded",
            allTEAMscanActivityTableData: {
              ...state.allTEAMscanActivityTableData,
              [timeFrame]: response,
            },
          };
        }
      )

      .addCase(getEppActivityTableData.pending, (state, { meta }) => {
        state.getEppActivityTableDataStatus = "loading";
        if (startActivityRequest(state.eppActivityTableDataOwner, meta)) {
          state.allEppActivityTableData = {};
        }
      })
      .addCase(getEppActivityTableData.rejected, (state, { meta }) => {
        if (meta.requestId !== state.eppActivityTableDataOwner.requestId) return;
        state.getEppActivityTableDataStatus = "failed";
      })
      .addCase(
        getEppActivityTableData.fulfilled,
        (
          state,
          {
            payload: { response, entityType, companyKey },
          }: PayloadAction<{
            response: EppActivityTableData;
            entityType: string;
            companyKey: string;
          }>
        ) => {
          if (companyKey !== state.eppActivityTableDataOwner.companyKey) return;
          return {
            ...state,
            getEppActivityTableDataStatus: "succeeded",
            allEppActivityTableData: {
              ...state.allEppActivityTableData,
              [entityType]: response,
            },
          };
        }
      )
      .addCase(getTEAMscanFeedbackResponses.pending, (state) => {
        state.getTeamScanFeedbackStatus = "loading";
      })
      .addCase(getTEAMscanFeedbackResponses.fulfilled, (state, { payload }) => {
        return {
          ...state,
          teamScanFeedback: payload,
          getTeamScanFeedbackStatus: "succeeded",
        };
      })
      .addCase(getTEAMscanFeedbackResponses.rejected, (state) => {
        state.getTeamScanFeedbackStatus = "failed";
      })
      .addCase(updateCompanyTimezone.pending, (state) => {
        state.updateCompanyTimezoneStatus = "loading";
      })
      .addCase(
        updateCompanyTimezone.fulfilled,
        (state, { payload: timezone }) => {
          if (!state.companySettings) return;
          return {
            ...state,
            updateCompanyTimezoneStatus: "succeeded",
            companySettings: {
              ...state.companySettings,
              timezone,
            },
          };
        }
      )
      .addCase(updateCompanyTimezone.rejected, (state) => {
        state.updateCompanyTimezoneStatus = "failed";
      })
      .addCase(deactivateAutomaticTeam.pending, (state) => {
        state.deactivateAutomaticTeamStatus = "loading";
      })
      .addCase(deactivateAutomaticTeam.fulfilled, (state) => {
        state.deactivateAutomaticTeamStatus = "succeeded";
      })
      .addCase(deactivateAutomaticTeam.rejected, (state) => {
        state.deactivateAutomaticTeamStatus = "failed";
      })
      .addCase(fetchIntegrationStatus.pending, (state, { meta }) => {
        state.fetchIntegrationStatusStatus[meta.arg] = "loading";
      })
      .addCase(fetchIntegrationStatus.fulfilled, (state, { payload }) => {
        state.integrationStatus[payload.provider] = payload;
        state.fetchIntegrationStatusStatus[payload.provider] = "succeeded";
      })
      .addCase(fetchIntegrationStatus.rejected, (state, { meta }) => {
        state.fetchIntegrationStatusStatus[meta.arg] = "failed";
      });
  },
});

// ------------------ Selectors ------------------
export const selectCompanySettings = (state: RootState) =>
  state.adminConsole.companySettings;
export const selectUpdateCompanySettingsStatus = (state: RootState) =>
  state.adminConsole.updateCompanySettingsStatus;

// This selector is used to hide the invite team leaders button, if the setting on who can create teams is either only admins or custom.
// Could change the behavior down the line to just check if the user is an admin and then have the userAccountId added automatically to the custom list.
export const selectUserIsAbleToInviteTeamLeaders = (state: RootState) => {
  const { companySettings } = state.adminConsole;
  return (
    companySettings &&
    companySettings.teamCreationAccess !== 2 &&
    companySettings.teamCreationAccess !== 4
  );
};
export const selectGetCompanySettingsStatus = (state: RootState) =>
  state.adminConsole.getCompanySettingsStatus;
export const selectDepartments = (state: RootState) =>
  state.adminConsole.departments;
export const selectGetDepartmentsStatus = (state: RootState) =>
  state.adminConsole.getDepartmentsStatus;
export const selectDepartmentTypes = (state: RootState) =>
  state.adminConsole.departmentTypes;
export const selectGetDepartmentTypesStatus = (state: RootState) =>
  state.adminConsole.getDepartmentTypesStatus;
export const selectCreateDepartmentStatus = (state: RootState) =>
  state.adminConsole.createDepartmentStatus;
export const selectCreatedDepartmentId = (state: RootState) =>
  state.adminConsole.createdDepartmentId;
export const selectCompanyInviteLink = (state: RootState) =>
  state.adminConsole.companyInviteLink;
export const selectCreateCompanyInviteLinkStatus = (state: RootState) =>
  state.adminConsole.createCompanyInviteLinkStatus;
export const selectDeleteDepartmentStatus = (state: RootState) =>
  state.adminConsole.deleteDepartmentStatus;
export const selectUpdateDepartmentStatus = (state: RootState) =>
  state.adminConsole.updateDepartmentStatus;
export const selectGetAnalyticsTextStatus = (state: RootState) =>
  state.adminConsole.getAnalyticsTextStatus;
export const selectAnalyticsText = (state: RootState) =>
  state.adminConsole.analyticsText;
export const selectGetSampleAnalyticsTextStatus = (state: RootState) =>
  state.adminConsole.getSampleAnalyticsTextStatus;
export const selectSampleAnalyticsText = (state: RootState) =>
  state.adminConsole.sampleAnalyticsText;
export const selectGetTextSynthesisStatus = (state: RootState) =>
  state.adminConsole.getTextSynthesisStatus;
export const selectTextSynthesis = (state: RootState) =>
  state.adminConsole.textSynthesis;
export const selectGetWordCloudStatus = (state: RootState) =>
  state.adminConsole.getWordCloudStatus;
export const selectWordCloud = (state: RootState) =>
  state.adminConsole.wordCloud;
export const selectIntegrationStatus = (state: RootState) =>
  state.adminConsole.integrationStatus;
export const selectFetchIntegrationStatusStatus = (state: RootState) =>
  state.adminConsole.fetchIntegrationStatusStatus;
export const selectGetTextInsightsStatus = (state: RootState) =>
  state.adminConsole.getTextInsightsStatus;
export const selectTextInsights = (state: RootState) =>
  state.adminConsole.textInsights;
export const selectFeedbackResponsesCount = (state: RootState) =>
  state.adminConsole.feedbackResponsesCount;
export const selectGetFeedbackResponsesCountStatus = (state: RootState) =>
  state.adminConsole.getFeedbackResponsesCountStatus;
export const selectScheduleTEAM360Status = (state: RootState) =>
  state.adminConsole.scheduleTEAM360Status;
export const selectTeamAndDepartmentLeads = (state: RootState) =>
  state.adminConsole.teamDepartmentLeads;
export const selectTeamAndDepartmentLeadIdsForLoggedInUser = (
  state: RootState
) => {
  const { teamDepartmentLeads } = state.adminConsole;
  const { currentUserAccountId } = state.global;
  if (!teamDepartmentLeads || !currentUserAccountId) return null; // If no teamDepartmentLeads or no currentUserAccountId, return null
  const { users } = teamDepartmentLeads;
  const teamAndDepartments = users?.[currentUserAccountId];
  if (!teamAndDepartments)
    return {
      teams: [],
      departments: [],
    }; // If no teamAndDepartments, return {}
  return teamAndDepartments;
};
export const selectGetTeamAndDepartmentLeadsStatus = (state: RootState) =>
  state.adminConsole.getTeamAndDepartmentLeadsStatus;
export const selectGetGenderInsightsStatus = (state: RootState) =>
  state.adminConsole.getGenderInsightsStatus;
export const selectGenderInsights = (state: RootState) =>
  state.adminConsole.genderInsights;
export const selectTeam360InstancePicked = (state: RootState) =>
  state.adminConsole.team360InstancePicked;
export const selectUpdateTeam360DateRangeStatus = (state: RootState) =>
  state.adminConsole.updatingTeam360DateRangeStatus;
export const selectOrganizationTeamAssessmentInstances = (state: RootState) =>
  state.adminConsole.organizationTeamAssessmentInstances;
export const selectGetTeamAssessmentInstancesStatus = (state: RootState) =>
  state.adminConsole.getTeamAssessmentInstancesStatus;
export const selectAssessmentInformation = (state: RootState) =>
  state.adminConsole.assessmentInformation;
export const selectGettingAssessmentInformationStatus = (state: RootState) =>
  state.adminConsole.gettingAssessmentInformationStatus;
export const selectTeam360CardsExpanded = (state: RootState) =>
  state.adminConsole.team360CardsExpanded;
export const selectTextInsightsErrorMessage = (state: RootState) =>
  state.adminConsole.textInsightsErrorMessage;
export const selectIsDepartmentsHidden = (state: RootState) =>
  state.adminConsole.isDepartmentsHidden;
export const selectCheckNewEmailStatus = (
  state: RootState // this is used for new emails that are being checked
) => state.adminConsole.checkSingleEmailStatus;
export const selectCheckUserEmailStatus = (
  state: RootState // this is used for check users that already exist in the system
) => state.adminConsole.checkUserEmailStatus;
export const selectNewCheckedEmails = (state: RootState) =>
  state.adminConsole.newCheckedEmails;
export const selectAiFeaturesEnabled = (state: RootState) =>
  state.adminConsole.aiFeaturesEnabled;
export const selectInviteCreatedUsersStatus = (state: RootState) =>
  state.adminConsole.inviteCreatedUsersStatus;
export const selectTestTakerMatches = (state: RootState) =>
  state.adminConsole.testTakeQueriedMatches;
export const selectGetTestTakerMatchesStatus = (state: RootState) =>
  state.adminConsole.getTestTakerMatchesStatus;
export const selectIsInviteModalOpen = (state: RootState) =>
  state.adminConsole.isInviteModalOpen;
export const selectHasCoachBoConversationHistoryEnabled = (state: RootState) =>
  state.adminConsole.hasCoachBoConversationHistoryEnabled;
export const selectUpdateVisibilitySettingsStatus = (state: RootState) =>
  state.adminConsole.updateVisibilitySettingsStatus;
export const selectVisibilityPermissionDifferCountStatus = (state: RootState) =>
  state.adminConsole.getVisibilityPermissionDifferCountStatus;
export const selectUserVisibilityUpdatedEntities = (state: RootState) =>
  state.adminConsole.userVisibilityUpdatedEntities;
export const selectTeamMembersWithPendingTeamScanSurvey = (state: RootState) =>
  state.adminConsole.teamMembersWithPendingTeamScanSurvey;
export const selectInvitingTeamMembersWithPendingTeamScanSurveyStatus = (
  state: RootState
) => state.adminConsole.invitingTeamMembersWithPendingTeamScanSurveyStatus;
export const selectHideSurveyTableBanner = (state: RootState) =>
  state.adminConsole.hideSurveyTableBanner;
export const selectBulkUpdatePendingTeamMembershipStatus = (state: RootState) =>
  state.adminConsole.bulkUpdatePendingTeamMembershipsStatus;
export const selectPendingTeamMemberships = (state: RootState) =>
  state.adminConsole.pendingTeamMemberships;
export const selectPendingTeamMemberStatus = (state: RootState) =>
  state.adminConsole.getPendingTeamMembershipsStatus;
export const selectDeactivatedUsers = (state: RootState) =>
  state.adminConsole.deactivatedUsers;
export const selectCheckedEditedEmails = (state: RootState) =>
  state.adminConsole.checkedEditedEmails;
export const selectGetTeamScanFeedbackStatus = (state: RootState) =>
  state.adminConsole.getTeamScanFeedbackStatus;
export const selectTeamScanFeedback = (state: RootState) =>
  state.adminConsole.teamScanFeedback;
export const selectAllOverallActivityTableData = (state: RootState) =>
  state.adminConsole.allOverallActivityTableData;
export const selectGetOverallActivityTableDataStatus = (state: RootState) =>
  state.adminConsole.getOverallActivityTableDataStatus;
export const selectAllTEAMscanActivityTableData = (state: RootState) =>
  state.adminConsole.allTEAMscanActivityTableData;
export const selectGetTEAMscanActivityTableDataStatus = (state: RootState) =>
  state.adminConsole.getTEAMscanActivityTableDataStatus;
export const selectAllEppActivityTableData = (state: RootState) =>
  state.adminConsole.allEppActivityTableData;
export const selectGetEppActivityTableDataStatus = (state: RootState) =>
  state.adminConsole.getEppActivityTableDataStatus;
export const selectUpdateCompanyTimezoneStatus = (state: RootState) =>
  state.adminConsole.updateCompanyTimezoneStatus;
export const selectDeactivateAutomaticTeamStatus = (state: RootState) =>
  state.adminConsole.deactivateAutomaticTeamStatus;

// ------------------ Actions ------------------
export const {
  clearScheduleTEAM360Status,
  removeUserLeader,
  addUserLeader,
  resetUpdateTeam360DateRangeStatus,
  setTeam360CardsExpanded,
  resetTextInsights,
  resetTextSynthesis,
  clearCreatedDepartmentId,
  setTeamScanAssessmentActiveStatus,
  toggleDisableDepartments,
  toggleAIFeaturesEnabled,
  setInviteModalOpen,
  toggleHasCoachBoConversationHistoryEnabled,
  updateCompanySettingsAction,
  onHideSurveyTableBanner,
  clearPendingTeamMemberships,
  resetEditedCheckedEmails,
  resetTeamScanFeedback,
  updateDepartmentsBasedOnWeeklyCheckInCustomList,
  resetCreateDepartmentStatus,
} = adminConsoleSlice.actions;

export default adminConsoleSlice.reducer;
